<template>
  <div class="w-96 p-8 space-y-2 font-intervariable">
    <!-- Embedded Checkout Component (Full Screen Mode) -->
    <div v-if="showEmbeddedCheckout">
      <div class="mb-4 text-center">
        <h3 class="text-lg font-semibold">Complete Your Purchase</h3>
        <p class="text-sm text-muted-foreground">
          Purchasing ${{ checkoutAmount }} ({{ dollarsToCredits(checkoutAmount) }} credits)
        </p>
      </div>
      
      <EmbeddedStripeCheckout
        :client-secret="checkoutClientSecret"
        :amount="checkoutAmount"
        :currency="checkoutCurrency"
        @success="onPaymentSuccess"
        @error="onPaymentError"
        @cancel="onPaymentCancel"
      />
    </div>

    <!-- Main Credits Interface (Hidden during checkout) -->
    <div v-else>
      <!-- Success/Status Message -->
      <div v-if="successMessage" class="text-center text-sm p-4 rounded-lg mb-4" :class="{
        'text-green-700 bg-green-50 border border-green-200': successMessage.includes('Successfully'),
        'text-yellow-700 bg-yellow-50 border border-yellow-200': successMessage.includes('cancelled'),
        'text-blue-700 bg-blue-50 border border-blue-200': !successMessage.includes('Successfully') && !successMessage.includes('cancelled')
      }">
        <div class="flex items-center justify-center gap-2">
          <Icon 
            :name="successMessage.includes('Successfully') ? 'heroicons:check-circle' : 
                   successMessage.includes('cancelled') ? 'material-symbols:info' : 
                   'heroicons:information-circle'" 
            class="w-4 h-4" 
          />
          {{ successMessage }}
        </div>
      </div>

      <!-- Credit Balance Display -->
      <div class="text-left space-y-0 w-full flex justify-between overflow-hidden">
        <div class="flex items-center gap-2 overflow-visible flex-1 min-w-0">
          <AnimatedNumber 
            :value="creditBalance" 
            :loading="initialLoading"
            :last-known-value="Math.floor(storedCredits * 10)"
            class="text-7xl font-bold text-primary tracking-tight font-nationalpark select-none whitespace-nowrap"
          />
        </div>

        <Button 
          @click="openFullManager" 
          variant="text" 
          size="icon"
          class="w-12 h-12 flex-shrink-0 text-muted-foreground hover:text-primary"
        >
          <Icon name="ic:round-bar-chart" class="w-5 h-5" />
        </Button>
      </div>
      
      <div class="flex gap-3 items-end select-none ml-1 mt-3">
        <div class="text-2xl text-primary select-none">
          Credits
          <span class="text-sm text-muted-foreground whitespace-nowrap">(
            $<AnimatedNumber 
              :value="balance" 
              :loading="initialLoading"
              :last-known-value="storedBalance"
              :format-options="{ 
                style: 'decimal', 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              }"
              class="inline whitespace-nowrap"
            /> Available)
          </span>
        </div>
      </div>

      <!-- Add Credits Section -->
      <div class="space-y-4 pt-8">
        <h3 class="text-sm text-muted-foreground select-none transition-all ml-1" :class="{'opacity-0 translate-x-10': showBuyButton}">Add Credits</h3>
        
        <div class="flex items-start gap-3 relative">
          <!-- Quick Purchase Buttons (Dollar Amounts) -->
          <Button
            v-for="pkg in creditPackages" 
            :key="pkg.cost"
            @click="purchaseCreditPackage(pkg.cost)"
            
            class="h-10 px-3 rounded-lg squircle-15 bg-white dark:bg-background text-primary hover:bg-foreground/90 hover:text-background dark:hover:bg-primary text-sm font-medium flex-shrink-0 select-none transition-all z-10 min-w-12"
            :class="{'!opacity-0 translate-x-10 pointer-events-none': showBuyButton}"
          >
            <span class="text-sm p-0">
              <div class="absolute top-0 -left-3 p-0 w-full h-full flex items-center justify-center pointer-events-none">
                <Icon v-if="clickedToPurchase === pkg.cost" name="lucide:loader-2" class="w-4 h-4 animate-spin" />
              </div>
              <span class="opacity-100" :class="{'!opacity-0': clickedToPurchase === pkg.cost}">
                ${{ pkg.cost }}
              </span>
            </span>
          </Button>
          
          <!-- Purchase Area Container (for click outside detection) -->
          <div ref="purchaseAreaRef" class="absolute right-0 flex items-center gap-2">
            <!-- Buy Button (appears on left of input) -->
            <Button 
              @click="purchaseCustomAmount" 
              
              class="h-10 px-3 rounded-xl squircle-15 bg-primary dark:bg-primary dark:text-background text-background hover:bg-black hover:text-background dark:hover:bg-white dark:hover:text-background text-sm font-medium flex-shrink-0 select-none transition-all"
              :class="{'!opacity-0 translate-x-10': !showBuyButton}"
            >
            <span v-if="!loading">
              Buy
            </span>
            <span v-else-if="!initialLoading">
                <Icon name="lucide:loader-2" class="w-4 h-4 animate-spin" />
            </span>
            <span v-else>
              Buy
            </span>
            
            </Button>
            
            <!-- Custom Amount Input -->
            <NumberField 
              v-model="customAmount"
              :min="1"
              :max="250"
              :step="1"
              :format-options="{
                style: 'currency',
                currency: 'AUD',
                currencyDisplay: 'symbol'
              }"
              class="w-20 min-w-24"
              @keydown.enter="purchaseCustomAmount"
            >
              <NumberFieldContent class="h-10">
                <NumberFieldDecrement />
                <NumberFieldInput 
                  placeholder="$10"
                  class="text-center text-xs bg-white dark:bg-background !rounded-xl"
                />
                <NumberFieldIncrement />
              </NumberFieldContent>
            </NumberField>
          </div>
        </div>
      </div>

      <!-- Credit Value Info -->
      <div class="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg mt-6">
        <strong>$1 = 10 credits</strong> • Get expert advice from WellSpring
      </div>

      <!-- Loading State -->
      <div v-if="loading && showEmbeddedCheckout" class="text-center text-sm text-muted-foreground py-2">
        <Icon name="lucide:loader-2" class="w-4 h-4 animate-spin mx-auto mb-2" />
        Processing purchase...
      </div>
      
      <!-- Error State -->
      <div v-if="error" class="text-center text-sm text-red-600 bg-red-50 p-3 rounded">
        {{ error }}
        <Button @click="clearError" variant="outline" size="sm" class="mt-2">
          Dismiss
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import CreditManager from '@/components/CreditManager.vue'
import EmbeddedStripeCheckout from '@/components/EmbeddedStripeCheckout.vue'
import AnimatedNumber from '@/components/ui/animated-number.vue'
import { NumberField, NumberFieldContent, NumberFieldInput, NumberFieldDecrement, NumberFieldIncrement } from '@/components/ui/number-field'
import { onClickOutside } from '@vueuse/core'
import { markRaw } from 'vue'

const { 
  credits, 
  balance,
  creditBalance: creditBalanceFromUseCredits,
  loading, 
  error, 
  purchaseCredits,
  fetchCredits,
  handlePaymentReturn,
  cleanPaymentParams
} = useCredits()

const userStore = useUserStore()
const { 
  credits: storedCredits, 
  balance: storedBalance,
  setCredits,
  setBalance
} = userStore

const { 
  purchaseWellSpringCreditsByDollars,
  dollarsToCredits,
  CREDITS_PER_DOLLAR,
  loading: wellspringLoading,
  error: wellspringError
} = useWellSpringBilling()

// Separate loading state for initial credit fetch vs purchases
const initialLoading = ref(true)

useSquircleObserver()

const { open: openBottomDrawer } = useBottomDrawer()

// Credit packages displayed as dollar amounts (more user-friendly)
const creditPackages = [
  { cost: 10, credits: 100 },   // $10 = 100 credits  
  { cost: 25, credits: 250 },   // $25 = 250 credits
  { cost: 50, credits: 500 }    // $50 = 500 credits
]

const customAmount = ref(null)
const successMessage = ref('')
const purchaseAreaRef = ref()

// Embedded checkout state
const showEmbeddedCheckout = ref(false)
const checkoutClientSecret = ref('')
const checkoutAmount = ref(0)
const checkoutCurrency = ref('aud')

const clickedToPurchase = ref(0)

const showBuyButton = ref(false)

const emit = defineEmits(['close'])

// Get credit balance in credits (not dollars) - using the same source as CreditManager
const creditBalance = creditBalanceFromUseCredits

// Handle payment return from Stripe checkout
onMounted(async () => {
  // Always fetch credits first to ensure we have current data
  await fetchCredits()
  
  // Set initial loading to false after first fetch
  initialLoading.value = false
  
  const paymentResult = handlePaymentReturn()
  
  if (paymentResult) {
    if (paymentResult.success) {
      successMessage.value = paymentResult.message
      
      // Clean URL parameters after showing success
      setTimeout(() => {
        cleanPaymentParams()
      }, 100)
      
      // Auto-close popover after success (optional)
      setTimeout(() => {
        emit('close')
      }, 3000)
      
    } else if (paymentResult.cancelled) {
      // Show cancelled message briefly
      successMessage.value = paymentResult.message
      
      // Clean URL parameters
      setTimeout(() => {
        cleanPaymentParams()
      }, 100)
      
      // Clear message after a moment
      setTimeout(() => {
        successMessage.value = ''
      }, 3000)
    }
  }
})

// Click outside functionality to reset form
onClickOutside(purchaseAreaRef, () => {
  if (loading.value) return
  clickedToPurchase.value = 0
  if (showBuyButton.value) {
    showBuyButton.value = false
    customAmount.value = null
  }
})

// Watch for custom amount changes
watch(customAmount, (newVal) => {
  showBuyButton.value = !!newVal
})

// Clear success message after a delay
watch(successMessage, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      successMessage.value = ''
    }, 5000) // Increased to 5 seconds for payment messages
  }
})

// Watch for credit balance changes and save to store
watch(credits, (newValue) => {
  if (newValue !== undefined && newValue !== null) {
    setCredits(newValue)
  }
}, { immediate: true })

// Watch for dollar balance changes and save to store
watch(balance, (newValue) => {
  if (newValue !== undefined && newValue !== null) {
    setBalance(newValue)
  }
}, { immediate: true })

onUnmounted(() => {
  clickedToPurchase.value = 0
  showBuyButton.value = false
  successMessage.value = ''
})

const clearError = () => {
  fetchCredits()
}

const purchaseCreditPackage = async (dollarAmount) => {
  console.log('💳 CreditsPopover: Starting credit package purchase', { 
    dollarAmount, 
    currentClickedToPurchase: clickedToPurchase.value,
    loading: loading.value,
    showEmbeddedCheckout: showEmbeddedCheckout.value 
  })
  
  clickedToPurchase.value = dollarAmount
  console.log('💳 CreditsPopover: Set clickedToPurchase to:', clickedToPurchase.value)
  
  try {
    // Use the standard purchaseCredits method which will handle route capture
    const result = await purchaseCredits(dollarAmount)
    
    if (result && typeof result === 'object' && result.embedded) {
      // Handle embedded checkout
      console.log('💳 CreditsPopover: Setting up embedded checkout', result)
      checkoutClientSecret.value = result.clientSecret
      checkoutAmount.value = result.amount
      checkoutCurrency.value = result.currency
      showEmbeddedCheckout.value = true
      // Don't clear clickedToPurchase here - let it show until checkout is complete
    } else if (!result) {
      console.error('💳 CreditsPopover: Purchase failed')
      clickedToPurchase.value = 0 // Clear on error
      // Error will be shown via the error ref from useCredits
    }
    // Note: For hosted checkout, user will be redirected (legacy support)
    
  } catch (err) {
    console.error('💳 CreditsPopover: Purchase error', err)
    clickedToPurchase.value = 0 // Clear on error
  }
}

const purchaseCustomAmount = async () => {
  if (!customAmount.value || customAmount.value <= 0) return
  
  const dollarAmount = customAmount.value
  console.log('💳 CreditsPopover: Starting custom amount purchase', { dollarAmount })
  
  try {
    const result = await purchaseCredits(dollarAmount)
    
    if (result && typeof result === 'object' && result.embedded) {
      // Handle embedded checkout
      console.log('💳 CreditsPopover: Setting up embedded checkout', result)
      checkoutClientSecret.value = result.clientSecret
      checkoutAmount.value = result.amount
      checkoutCurrency.value = result.currency
      showEmbeddedCheckout.value = true
      
      // Reset form
      customAmount.value = null
      showBuyButton.value = false
    } else if (!result) {
      console.error('💳 CreditsPopover: Purchase failed')
    }
    // Note: For hosted checkout, user will be redirected (legacy support)
    
  } catch (err) {
    console.error('💳 CreditsPopover: Custom purchase error', err)
  }
}

// Handle embedded checkout events
const onPaymentSuccess = async (paymentDetails) => {
  console.log('💳 CreditsPopover: Payment successful', paymentDetails)
  
  clickedToPurchase.value = 0 // Clear spinner on success
  showEmbeddedCheckout.value = false
  successMessage.value = `Successfully purchased $${paymentDetails.amount} credits!`
  
  // Refresh credits immediately to update navbar
  await fetchCredits()
  
  // Auto-close popover after success
  setTimeout(() => {
    emit('close')
  }, 2000)
}

const onPaymentError = (error) => {
  console.error('💳 CreditsPopover: Payment error', error)
  clickedToPurchase.value = 0 // Clear spinner on error
  showEmbeddedCheckout.value = false
  // Error will be shown in the embedded component
}

const onPaymentCancel = () => {
  console.log('💳 CreditsPopover: Payment cancelled')
  clickedToPurchase.value = 0 // Clear spinner on cancel
  showEmbeddedCheckout.value = false
  // Reset checkout state
  checkoutClientSecret.value = ''
  checkoutAmount.value = 0
}

const openFullManager = () => {
  emit('close')
  openBottomDrawer({
    title: '',
    component: markRaw(CreditManager)
  })
}
</script> 